/**
 * Polyfill runtime per Safari/WebKit < 16
 *
 * @version 1.0.0 (FE v2.172.0)
 *
 * PERCHE':
 * Il bundle passa il parsing anche su Safari 15.x (la SINTASSI e' supportata),
 * ma alcune API recenti non esistono e si rompono solo quando vengono chiamate.
 * Caso reale (21/09/2026, iPad 8a gen non aggiornato): Vuetify chiama
 * `Array.from(...).findLast(...)` nella `hide()` degli overlay -> TypeError alla
 * prima chiusura di un menu/dialog -> stack overlay incoerente, tendine che non
 * si aprono piu', schermata "Application Error".
 *
 * COSA COPRE (solo metodi puri, implementazione da spec, niente dipendenze):
 *   Safari 15.4 -> Array/TypedArray findLast, findLastIndex, at; String at; Object.hasOwn
 *   Safari 16.0 -> Array toReversed, toSorted, toSpliced, with
 *
 * Ogni polyfill e' installato SOLO se il metodo manca, come proprieta' non
 * enumerabile (come quelle native: nessun effetto su for...in).
 *
 * DEVE essere il primo import di main.ts.
 */

type AnyFn = (...args: any[]) => any

function define(target: object, name: string, fn: AnyFn): void {
  if (name in target) return
  Object.defineProperty(target, name, {
    value: fn,
    writable: true,
    configurable: true,
    enumerable: false,
  })
}

function toIntegerOrInfinity(v: unknown): number {
  const n = Number(v)
  if (Number.isNaN(n)) return 0
  return Math.trunc(n)
}

function relativeIndex(len: number, index: unknown): number {
  const i = toIntegerOrInfinity(index)
  return i >= 0 ? i : len + i
}

// -----------------------------------------------------------------------------
// findLast / findLastIndex / at — Array + TypedArray + String
// -----------------------------------------------------------------------------

function findLast(this: ArrayLike<unknown>, predicate: AnyFn, thisArg?: unknown) {
  for (let i = this.length - 1; i >= 0; i--) {
    if (predicate.call(thisArg, this[i], i, this)) return this[i]
  }
  return undefined
}

function findLastIndex(this: ArrayLike<unknown>, predicate: AnyFn, thisArg?: unknown) {
  for (let i = this.length - 1; i >= 0; i--) {
    if (predicate.call(thisArg, this[i], i, this)) return i
  }
  return -1
}

function at(this: ArrayLike<unknown>, index: unknown) {
  const k = relativeIndex(this.length, index)
  return k < 0 || k >= this.length ? undefined : this[k]
}

define(Array.prototype, 'findLast', findLast)
define(Array.prototype, 'findLastIndex', findLastIndex)
define(Array.prototype, 'at', at)

// Tutti i TypedArray condividono lo stesso prototipo astratto
const TypedArrayProto = Object.getPrototypeOf(Int8Array.prototype)
if (TypedArrayProto) {
  define(TypedArrayProto, 'findLast', findLast)
  define(TypedArrayProto, 'findLastIndex', findLastIndex)
  define(TypedArrayProto, 'at', at)
}

define(String.prototype, 'at', function (this: string, index: unknown) {
  const s = String(this)
  const k = relativeIndex(s.length, index)
  return k < 0 || k >= s.length ? undefined : s.charAt(k)
})

// -----------------------------------------------------------------------------
// Object.hasOwn
// -----------------------------------------------------------------------------

define(Object, 'hasOwn', function (obj: object, key: PropertyKey) {
  if (obj == null) throw new TypeError('Cannot convert undefined or null to object')
  return Object.prototype.hasOwnProperty.call(Object(obj), key)
})

// -----------------------------------------------------------------------------
// Change-array-by-copy (Safari 16.0)
// -----------------------------------------------------------------------------

define(Array.prototype, 'toReversed', function (this: unknown[]) {
  return Array.prototype.slice.call(this).reverse()
})

define(Array.prototype, 'toSorted', function (this: unknown[], compareFn?: AnyFn) {
  if (compareFn !== undefined && typeof compareFn !== 'function') {
    throw new TypeError('The comparison function must be either a function or undefined')
  }
  return Array.prototype.slice.call(this).sort(compareFn)
})

define(Array.prototype, 'toSpliced', function (this: unknown[], ...args: any[]) {
  const copy = Array.prototype.slice.call(this)
  if (args.length === 0) return copy
  ;(copy.splice as AnyFn).apply(copy, args)
  return copy
})

define(Array.prototype, 'with', function (this: unknown[], index: unknown, value: unknown) {
  const len = this.length
  const k = relativeIndex(len, index)
  if (k < 0 || k >= len) throw new RangeError('Invalid index : ' + index)
  const copy = Array.prototype.slice.call(this)
  copy[k] = value
  return copy
})

export {}
